import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * NativeSelect — a styled native `<select>`.
 *
 * Kept native on purpose where a short fixed list of self-explanatory options is picked inline
 * (the active farm): the OS picker on phones, type-ahead, and form semantics come for free, and
 * tests drive it with `selectOptions`. Use the Radix `Select` when an option needs more than its
 * name — a member's role and what it may do (`SelectItem description`).
 *
 * Preflight is excluded from the DS build, so the UA chrome is reset here (appearance, border,
 * focus ring, font-family — a `<select>` does not inherit the document font, #1927) and the
 * chevron is drawn by the wrapper.
 */
export interface NativeSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  size?: never;
  controlSize?: 'sm' | 'default';
}

export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, controlSize = 'default', children, ...props }, ref) => (
    <span className="tw:relative tw:inline-flex tw:items-center">
      <select
        ref={ref}
        className={cn(
          'tw:w-full tw:cursor-pointer tw:appearance-none tw:rounded-md tw:border tw:border-input tw:bg-card tw:pl-3 tw:pr-8 tw:font-sans tw:text-sm tw:text-foreground tw:shadow-sm tw:transition-colors',
          'tw:hover:bg-accent tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
          'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
          controlSize === 'sm' ? 'tw:h-8' : 'tw:h-9',
          className
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="tw:pointer-events-none tw:absolute tw:right-2.5 tw:size-4 tw:text-muted-foreground"
      />
    </span>
  )
);
NativeSelect.displayName = 'NativeSelect';
